import * as Sentry from '@sentry/react';
import {
  monolithTracePropagationTargets,
  replayPrivacyOptions,
  sampleRate,
  scrubBreadcrumb,
  scrubLog,
  scrubRecordingEvent,
  scrubSpan,
  scrubUrlsIntegration,
} from 'sentry-privacy';

import {
  COMMIT_SHA,
  REACT_APP_SENTRY_DSN,
  REACT_APP_SENTRY_LOGS_ENABLED,
  REACT_APP_SENTRY_PROFILES_SAMPLE_RATE,
  REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
} from 'runtimeEnvVars';

import { domainMeta } from './domainMeta';
import { isReloadedChunkError } from './reloadOnChunkLoadError';

/**
 * Browser error reporting, tracing, replay, profiling and logs for the
 * recruiter app.
 *
 * Dormant unless REACT_APP_SENTRY_DSN is baked into the build. That gate is the
 * whole switch: a local `npm run dev`, a Storybook build and a Jest run all
 * leave it unset and therefore start no client at all.
 *
 * One bundle serves every tenant — prod, uat, staging and the dev clusters all
 * fetch the same sha7 from S3 — so the DSN is a build-time constant while the
 * environment and tenant are read from the hostname at load. Anything derived
 * from the environment has to be read here, not at build time.
 */

export function initSentry(): void {
  if (!REACT_APP_SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: REACT_APP_SENTRY_DSN,
    environment: domainMeta.environment,
    // Ties an event to the build that produced it, and to the source maps
    // uploaded under the same name. Release health and suspect commits both key
    // off it.
    release: COMMIT_SHA,
    tracesSampleRate: sampleRate(REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0.01),
    // Relative to the sampled traces, not to all page loads. Inert until the
    // frontend worker serves `Document-Policy: js-profiling`.
    profilesSampleRate: sampleRate(REACT_APP_SENTRY_PROFILES_SAMPLE_RATE, 1),
    // Nothing is recorded routinely. A session that produces an error is
    // recorded whatever this is, which is the case worth watching back.
    replaysSessionSampleRate: sampleRate(
      REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0,
    ),
    replaysOnErrorSampleRate: sampleRate(
      REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1,
    ),
    tracePropagationTargets: monolithTracePropagationTargets(
      domainMeta.monolithOrigin,
      Sentry.getActiveSpan,
    ),
    // `scrubBreadcrumb` drops console breadcrumbs, and `scrubLog` keeps a
    // console log's text but not its arguments: what the app logs includes
    // `ApiError.body`, which is what a candidate typed. On unless the build
    // sets the variable to "false"; turning it off takes a rebuild.
    enableLogs: REACT_APP_SENTRY_LOGS_ENABLED !== 'false',
    beforeSendLog: scrubLog,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      // A replay carries whatever was on screen, and these pages carry
      // applicant PII — names, contact details, documents.
      // `replayPrivacyOptions` keeps text, inputs and media masked and blocks
      // contact links, document downloads and frames outright.
      Sentry.replayIntegration({
        // Scrubs the navigation and network entries the recording holds.
        //
        // Known limit, accepted: the page URL, query values such as search
        // terms included, reaches the replay verbatim through rrweb's meta
        // event, which passes no SDK hook. So does the `href` of a link with
        // no query string. On-page text stays masked.
        beforeAddRecordingEvent: scrubRecordingEvent,
        ...replayPrivacyOptions,
      }),
      // Fed by `utils/sentryUser`. The flags on an event are the ones the
      // session was evaluated under, which is usually the first question asked
      // of an issue that only some accounts see.
      Sentry.featureFlagsIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Page and request URLs carry credentials and search terms.
      scrubUrlsIntegration(),
    ],
    beforeBreadcrumb: scrubBreadcrumb,
    // Standalone spans, INP among them, reach no event processor.
    beforeSendSpan: scrubSpan,
    // No email, no name, no IP address. A recruiter's uuid is what "users
    // affected" counts, and it is attached separately.
    sendDefaultPii: false,
    ignoreErrors: [
      // `utils/axios` rejects with this text when a request gets no response:
      // the recruiter went offline, the connection reset, or the page left
      // mid-request. A caller that does not catch it names no code to fix,
      // and the failed request is already a breadcrumb on any other event.
      /^Non-Error promise rejection captured with value: Network error detected\. Please try again\.$/,
    ],
    // The page is already reloading to fetch the chunk again.
    beforeSend: (event, hint) =>
      isReloadedChunkError(hint.originalException) ? null : event,
  });

  // Every issue in this project belongs to exactly one customer's hostname.
  Sentry.setTag('tenant', domainMeta.tenant);
}
